import { Form, Tooltip, Typography } from "antd";
import BaseModal from "components/BaseModal";
import message from "components/CustomMessage";
import { HelpIcon } from "components/Icons";
import Vocabulary from "components/Vocabulary";
import { MODAL_SIZE_LARGE } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import {
  FeatureFlagContentValuesType,
  useFeatureFlag,
} from "contexts/FeatureFlagContext";
import _, { isEmpty } from "lodash";
import React, {
  ChangeEvent,
  ForwardedRef,
  MutableRefObject,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getEmptyCancellableRequest } from "restAPI";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import { FieldTypes, generateFormFields } from "utils/formUtils";
import { SEVERITY_NAMES } from "views/Findings/Constants";

import {
  CUSTOM_FILED_SUPPORTED_INTEGRATIONS,
  GenericIntegrationTicketDefaultValues,
  genericIntegrationTicketProductSubproductAppendAll,
} from "../genericIntegrationTicketConstants";
import {
  ResolvedFindingStatusNames,
  generateResponse,
  integrationTicketingToolsProductOptionsMutator,
  useUnifiedMultiTemplateOptions,
  useUnifiedSingleTemplateOptions,
} from "../genericIntegrationTicketUtils";
import AsanaCustomFields from "./AsanaCustomFields";

const INITIAL_VALUES = {
  product: ["all"],
  subProduct: ["all"],
  critical: "Highest",
  high: "High",
  medium: "Medium",
  low: "Low",
  info: "Lowest",
};

const CONFIG_CHANGE_MESSAGE =
  "If You select a different Form, Organisation or Issue Type. Please review the settings and mapping on this screen before saving to ensure issues in the new Form, Organisation or Issue Type work as expected";

function getInitialData(
  data: GenericIntegrationTicketDefaultValues = {},
): GenericIntegrationTicketDefaultValues {
  data = {
    ...data,
    labels: data?.labels || ["Armorcode_Associated_Finding"],
    ...data.properties,
    subProduct: !isEmpty(data?.subProductIds) ? data?.subProductIds : ["all"],
    product: !isEmpty(data?.product) ? data.product : ["all"],
    ...(data.transitions
      ? { transitions: JSON.stringify(data.transitions) }
      : {}),
  };

  const defaultValues = { ...INITIAL_VALUES };
  Object.keys(data).forEach((key) => {
    // TODO resolve TS
    // @ts-expect-error
    if (_.isPlainObject(data[key]) && data[key].id) {
      // @ts-expect-error
      defaultValues[key] = data[key].id;
      // @ts-expect-error
    } else if (data?.[key]) {
      // @ts-expect-error
      defaultValues[key] = data?.[key];
    }
  });
  return defaultValues;
}

const { Link } = Typography;

type AsanaConfigMappingFormProps = {
  loginConfigId: number;
  onUpdateConfigComponent: (value: boolean) => void;
  config?: GenericIntegrationTicketDefaultValues;
  url?: string;
  name: string;
};

const AsanaConfigMappingForm = (
  {
    loginConfigId,
    onUpdateConfigComponent,
    url,
    name,
    ...props
  }: AsanaConfigMappingFormProps,
  ref: ForwardedRef<any>,
) => {
  const [initialData] = useState(() => getInitialData(props.config));
  const isEdit = Boolean(props.config?.id);
  const [form] = Form.useForm();
  const [data, setData] = useState(initialData);
  const [isCustomFieldsLoading, setIsCustomFieldsLoading] = useState(false);
  const [transitionStatusOptions, setTransitionStatusOptions] = useState([]);
  const [isResolvedStatusDisabled, setIsResolvedStatusDisabled] = useState(
    !data.markResolved,
  );
  const [markReopen, setMarkReopen] = useState(data.markReopen);
  const [priorities, setPriorities] = useState([]);
  const featureFlags = useFeatureFlag();
  const toolName = name.toUpperCase();
  const { getUnifiedSingleTemplates, unifiedSingleDefaultTemplate } =
    useUnifiedSingleTemplateOptions();
  const { getUnifiedMultiTemplates, unifiedMultiDefaultTemplate } =
    useUnifiedMultiTemplateOptions();
  const customFieldSupportedInterations = useMemo(
    () =>
      _.compact(
        _.map(CUSTOM_FILED_SUPPORTED_INTEGRATIONS, (entry) => {
          const featureFlag = (entry as any).featureFlag;
          if (
            (featureFlag &&
              featureFlags[
                featureFlag as keyof FeatureFlagContentValuesType
              ]) ||
            !featureFlag
          ) {
            return entry.tool;
          }
          return null;
        }),
      ),
    [featureFlags],
  );

  const thisRef: MutableRefObject<any> = useRef();
  const customFieldsValueRef: MutableRefObject<any> = useRef({});
  const issueTypesRef: MutableRefObject<any> = useRef(
    props.config?.configMetaData?.issueTypeMetaData
      ? {
          name: props.config.configMetaData.issueTypeMetaData.name,
          id: props.config.configMetaData.issueTypeMetaData.id,
        }
      : {},
  );

  const getOrganizations = useCallback(() => {
    const { promise, cancel } = ToolsIntegrationsAPI.getZendeskOrganizations({
      loginConfigId,
    });
    const updatedPromise = promise.then((response) => {
      return response.data;
    });

    return { promise: updatedPromise, cancel };
  }, [loginConfigId]);

  useEffect(() => {
    let cancelTransionStatusApi = _.noop;
    const getTransitionStatusOptions = async () => {
      if (!loginConfigId || !data.issueType) {
        return false;
      }
      const requestPayload = {
        loginConfigId,
        issueType: data.issueType,
      };
      const {
        promise: transitionStatusPromise,
        cancel: transitionStatusReject,
      } =
        ToolsIntegrationsAPI.getJiraCloseTransitionStatusOptions(
          requestPayload,
        );
      cancelTransionStatusApi = transitionStatusReject;
      const statusOptions = await transitionStatusPromise;
      cancelTransionStatusApi = _.noop;
      setTransitionStatusOptions(statusOptions);
    };

    getTransitionStatusOptions();
    return cancelTransionStatusApi;
  }, [loginConfigId, data.issueType]);

  const getIssueTypes = useCallback(
    (params) => {
      if (!loginConfigId) {
        return { promise: Promise.resolve([]), cancel: _.noop };
      }
      return ToolsIntegrationsAPI.getTicketSystemToolIssueTypes({
        loginConfigId,
      });
    },
    [loginConfigId],
  );

  const onOk = async () => {
    await form.validateFields();
    const formData = { ...form.getFieldsValue(), ticketSystemType: "ASANA" };
    formData.ticketClosureStatus = _.isEmpty(formData.ticketClosureStatus)
      ? null
      : formData.ticketClosureStatus;
    formData.customFields = [];
    formData.organisationName = data.organisationName;
    const configValues: any = await generateResponse(
      formData,
      featureFlags,
      toolName,
      props.config?.id,
      loginConfigId,
      url,
      thisRef.current?.getCustomFields(),
    );

    if (!isEmpty(issueTypesRef.current)) {
      configValues.configValues = {
        ...configValues.configValues,
        metaData: {
          issueTypeMetaData: {
            id: issueTypesRef.current.id,
            name: issueTypesRef.current.name,
          },
        },
      };
    }

    if (!_.isPlainObject(configValues)) {
      return Promise.reject("Mapping already exist");
    }
    await ToolsIntegrationsAPI[
      !isEdit ? "integrateTicketSystemTool" : "updateTicketSystemToolMapping"
    ](configValues.configValues, props.config?.id).promise;
    message.success(`Asana Mapping saved`);
    setData({});
    onUpdateConfigComponent && onUpdateConfigComponent(false);
  };

  useEffect(() => {
    if (featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING || !data.issueType) {
      return;
    }
    setPriorities([]);
    const { cancel, promise } =
      ToolsIntegrationsAPI.getTicketSystemToolPriorities({
        issueType: data.issueType,
        loginConfigId,
      });
    promise.then((response) => {
      if (!isEdit) {
        const severities: any = {};
        const severityNames = Object.keys(SEVERITY_NAMES);
        response.forEach((entry: any, index: number) => {
          severities[severityNames[index]] = entry.id;
        });

        if (severityNames.length > response.length) {
          for (
            let index = response.length;
            index < severityNames.length;
            index++
          ) {
            severities[severityNames[index]] =
              severities[severityNames[index - 1]];
          }
        }
        form.setFieldsValue(severities);
      }
      setPriorities(response);
    });
    return cancel;
  }, [
    isEdit,
    data.issueType,
    loginConfigId,
    form,
    featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
  ]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        ticketUnifiedSingleTemplateId: unifiedSingleDefaultTemplate,
        ticketUnifiedMultipleTemplateId: unifiedMultiDefaultTemplate,
      });
    }
  }, [isEdit, form, unifiedSingleDefaultTemplate, unifiedMultiDefaultTemplate]);

  const resetSubProductsField = useCallback(
    (values) => {
      setData((data: GenericIntegrationTicketDefaultValues) => ({
        ...data,
        product: values,
      }));
      form.setFieldsValue({ subProduct: ["all"] });
    },
    [form],
  );

  const getLabels = useCallback(
    (params) => {
      if (!loginConfigId) {
        return getEmptyCancellableRequest([]);
      }
      return ToolsIntegrationsAPI.getJiraLabels({ loginConfigId, ...params });
    },
    [loginConfigId],
  );

  const commonSeverityAsanaProps = useMemo(
    () => ({
      colSpan: 5,
      className: "m-b-0",
      type: FieldTypes.SELECT,
      style: { flex: "0 0 20%" },
      options: priorities,
      loading: _.isNil(priorities),
      required: true,
    }),
    [priorities],
  );

  const modalProps = {
    title: `${isEdit ? "Edit" : "Add"} Asana Mapping`,
    width: MODAL_SIZE_LARGE,
    onOk,
    disabled:
      !hasPermission(
        isEdit
          ? PermissionSetMap.EDIT_TOOL_CONFIG
          : PermissionSetMap.WRITE_TOOLS,
      ) || isCustomFieldsLoading,
  };

  // if more than one product or All product is selected then subproduct should be disabled
  // this would work as previous, just will get the product id from array, as product is multi select
  const productId =
    data.product && data.product[0] !== "all" ? data.product[0] : null;

  const onCreateSubproduct = useCallback(
    (createdSubproduct) => {
      const subProductValues = form.getFieldValue("subProduct");
      form.setFieldsValue({
        subProduct: [...subProductValues, createdSubproduct.id],
      });
      setData((prevdata) => ({
        ...prevdata,
        subProduct: [...subProductValues, createdSubproduct.id],
      }));
    },
    [form],
  );

  const onCreateProduct = useCallback(
    (createdProduct) => {
      const productsAfterCreate = [
        ...form.getFieldValue("product"),
        createdProduct.id,
      ];
      form.setFieldsValue({
        product: productsAfterCreate,
        subProduct: ["all"],
      });
      setData((data) => ({
        ...data,
        product: productsAfterCreate,
        subProduct: ["all"],
      }));
    },
    [form],
  );

  const productSubProductFields = useMemo(
    () => [
      {
        name: "product",
        label: <Vocabulary name="product" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        // disabled:isEdit,
        props: {
          dropdownType: "product",
          showAddButton: true,
          optionsMutator: (
            options: [{ label: string; value: number | string }],
          ) =>
            integrationTicketingToolsProductOptionsMutator(
              options,
              form.getFieldValue("product"),
            ),
          onChange: resetSubProductsField,
          onCreate: onCreateProduct,
          mode: "multiple",
        },
      },
      {
        name: "subProduct",
        label: <Vocabulary name="subProduct" />,
        type: FieldTypes.PROD_SUBPROD_SELECT_ADD,
        required: true,
        colSpan: 12,
        disabled: data.product && data.product.length > 1,
        props: {
          productId: productId,
          showAddButton: Boolean(productId),
          dropdownType: "subProduct",
          onCreate: onCreateSubproduct,
          onChange: (values: any[]) => {
            if (values.includes("all")) {
              form.setFieldsValue({ subProduct: ["all"] });
            }
          },
          optionsMutator: (options: any[]) => {
            const updatedOptions =
              genericIntegrationTicketProductSubproductAppendAll(options);
            const selectedValues = form.getFieldValue("subProduct") || [];
            const isAllSelected = selectedValues.includes("all");

            return updatedOptions.map((option) => ({
              ...option,
              disabled: isAllSelected && option.value !== "all",
            }));
          },

          mode: "multiple",
        },
      },
    ],
    [
      form,
      productId,
      resetSubProductsField,
      onCreateSubproduct,
      onCreateProduct,
      data.product,
    ],
  );

  const parseIssueTypeOptions = useCallback((options) => {
    const updatedOptions = _.map(options, (option) => ({
      label: option.name,
      value: option.id,
    }));
    if (!isEmpty(issueTypesRef.current)) {
      return _.uniqBy(
        [
          ...updatedOptions,
          {
            label: issueTypesRef.current.name,
            value: issueTypesRef.current.id,
          },
        ],
        "value",
      );
    }
    return updatedOptions;
  }, []);

  const handleMarkResolveChecked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const checked = !data.issueType || !event.target.checked;
      setIsResolvedStatusDisabled(checked);
    },
    [data.issueType],
  );

  const handleMarkReopen = useCallback((event) => {
    const checked = event.target.checked;
    setMarkReopen(checked);
  }, []);

  const findingField = useMemo(
    () => [
      {
        render: (props: any) => <div {...props} />,
        className: "p-y-sm",
      },
      {
        name: "markResolved",
        label: (
          <>
            Close Ticket when Finding is <Vocabulary name={"mitigated"} />
            <Tooltip
              title={
                <>
                  Automatically closes Asana ticket when an issue is fixed. The
                  other states that may trigger ticket close are{" "}
                  {<ResolvedFindingStatusNames />} based on the global
                  configuration as provided <br />
                  <Link href="#/globalsettings?activeStep=step17">
                    Click here
                  </Link>
                </>
              }
              placement="top"
            >
              <HelpIcon />
            </Tooltip>
          </>
        ),
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkResolveChecked,
        },
      },
      {
        name: "resolvedStatus",
        label: "Close Transition Name",
        help: `Close Transition Name should be set as the final (close) status in your defined Asana workflow for your project. For example 'CLOSED', 'RESOLVED' or 'DONE'.`,
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        required: !isResolvedStatusDisabled,
        disabled: isResolvedStatusDisabled,
        colSpan: 12,
      },
      {
        name: "markReopen",
        label: "Re-open ticket if finding remains unresolved from a scan",
        type: FieldTypes.BOOLEAN,
        colSpan: 12,
        inline: true,
        props: {
          onChange: handleMarkReopen,
        },
        className: "m-t-l",
      },
      {
        name: "reopenStatus",
        label: "Re-open Transition Name",
        type: FieldTypes.SELECT,
        options: transitionStatusOptions,
        disabled: !markReopen,
        required: markReopen,
        colSpan: 12,
        help: (
          <>
            Asana status(transition) which can be directly assigned from closed
            status(transition)
          </>
        ),
        props: {
          allowClear: true,
        },
      },
      {
        name: "labels",
        label: "Tags",
        help: `These "tags" will be added to the tickets.`,
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        props: {
          mode: "tags",
          api: getLabels,
        },
        rules: [
          {
            validator: (_: any, values: string[]) => {
              const regEx = /\s/;
              if (values) {
                for (let i = 0; i < values.length; i += 1) {
                  if (regEx.test(values[i])) {
                    return Promise.reject(
                      "The label can't contain blank spaces.",
                    );
                  }
                }
              }
              return Promise.resolve();
            },
          },
        ],
      },
    ],
    [
      handleMarkResolveChecked,
      isResolvedStatusDisabled,
      transitionStatusOptions,
      handleMarkReopen,
      markReopen,
      getLabels,
    ],
  );

  const asanaFields = useMemo(
    () => [
      ...productSubProductFields,
      {
        name: "organisation",
        label: "Workspace",
        colSpan: 12,
        type: FieldTypes.SELECT,
        required: true,
        props: {
          api: getOrganizations,
          onChange: (
            value: string,
            field: any,
            selectedOption: { label: string; value: string },
          ) => {
            isEdit && message.warn(CONFIG_CHANGE_MESSAGE);
            form.setFieldsValue({ projectKey: null });
            setData((prevData) => ({
              ...prevData,
              organisation: value,
              organisationName: selectedOption.label,
              projectKey: undefined,
            }));
          },
        },
      },
      {
        name: "issueType",
        label: "Issue Type",
        colSpan: 12,
        type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
        required: true,
        props: {
          api: getIssueTypes,
          resetOptionsProp: getIssueTypes,
          parseSelectOptions: parseIssueTypeOptions,
          onChange: (
            value: string,
            listField: any,
            selectedOption: { label: string; value: string },
          ) => {
            isEdit && message.warn(CONFIG_CHANGE_MESSAGE);
            setData((data: GenericIntegrationTicketDefaultValues) => ({
              ...data,
              issueType: value,
            }));
            issueTypesRef.current = {
              id: selectedOption.value,
              name: selectedOption.label,
            };
          },
        },
      },
      ...(featureFlags.UNIFIED_TICKET_TEMPLATE
        ? [
            {
              name: "ticketUnifiedSingleTemplateId",
              label: "Single Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedSingleTemplates,
              },
            },
            {
              name: "ticketUnifiedMultipleTemplateId",
              label: "Multi-Finding Template",
              colSpan: 12,
              type: FieldTypes.SELECT,
              props: {
                api: getUnifiedMultiTemplates,
              },
            },
          ]
        : []),
      ...(featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING
        ? []
        : [
            {
              label: `ArmorCode Severity -> Asana Priority`,
              type: FieldTypes.GROUP,
              required: false,
              fields: [
                {
                  name: "critical",
                  label: <Vocabulary name="critical" />,
                  ...commonSeverityAsanaProps,
                },
                {
                  name: "high",
                  label: <Vocabulary name="high" />,
                  ...commonSeverityAsanaProps,
                },
                {
                  name: "medium",
                  label: <Vocabulary name="medium" />,
                  ...commonSeverityAsanaProps,
                },
                {
                  name: "low",
                  label: <Vocabulary name="low" />,
                  ...commonSeverityAsanaProps,
                },
                {
                  name: "info",
                  label: <Vocabulary name="info" />,
                  ...commonSeverityAsanaProps,
                },
              ],
            },
          ]),
      ...findingField,
    ],
    [
      commonSeverityAsanaProps,
      featureFlags.UNIFIED_TICKET_TEMPLATE,
      findingField,
      getIssueTypes,
      getUnifiedMultiTemplates,
      getUnifiedSingleTemplates,
      productSubProductFields,
      isEdit,
      getOrganizations,
      form,
      featureFlags.TICKETING_CUSTOM_SEVERITY_RISK_RATING,
      parseIssueTypeOptions,
    ],
  );

  //TODO need to resolve TS
  return (
    <BaseModal
      ref={ref}
      modalProps={modalProps}
      // @ts-expect-error
      afterClose={props.afterClose}
    >
      <Form form={form} initialValues={data} layout="vertical">
        {generateFormFields(asanaFields)}
      </Form>
      {customFieldSupportedInterations.indexOf("Asana") !== -1 ? (
        <AsanaCustomFields
          ref={thisRef}
          form={form}
          loginConfigId={loginConfigId}
          initialValues={data}
          configId={props.config?.id}
          changeMode={isEdit}
          toolName={toolName}
          customFieldsValueRef={customFieldsValueRef}
          onLoadingChange={setIsCustomFieldsLoading}
        />
      ) : null}
    </BaseModal>
  );
};

export default forwardRef(AsanaConfigMappingForm);
